import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchConveyancingDetailByCase, updateConveyancingDetail,
  calculateConveyancingFees, fetchConveyancingCostLetterPdf,
  fetchConveyancingFeeSnapshotPdf,
  fetchConveyancingFiaStatus,
  fetchConveyancingParties, createConveyancingParty,
  updateConveyancingParty, deleteConveyancingParty,
  downloadClosingStatement,
  createConveyancingInvoice,
  downloadBursTD1Pdf,
  fetchProperties,
} from '../api';
import { formatCurrency } from '../utils/currency';
import DeedInstrumentsList from './DeedInstrumentsList';
import SendPdfButton from './shared/SendPdfButton';

const TRANSACTION_TYPES = [
  ['TRANSFER', 'Transfer'],
  ['BOND_REGISTRATION', 'Bond Registration'],
  ['BOND_CANCELLATION', 'Bond Cancellation'],
  ['SECTIONAL_TITLE', 'Sectional Title'],
  ['NOTARIAL_BOND', 'Notarial Bond'],
  ['COMBINED', 'Combined (Transfer + Bond)'],
];

const RISK_RATINGS = [
  ['LOW', 'Low'], ['MEDIUM', 'Medium'], ['HIGH', 'High'], ['PEP', 'PEP'],
];

const ROLE_LABELS = {
  SELLER: 'Seller / Transferor',
  BUYER: 'Buyer / Transferee',
  BOND_HOLDER: 'Mortgagee / Bond Holder',
  GUARANTOR: 'Guarantor / Surety',
  ESTATE_AGENT: 'Estate Agent',
  EXECUTOR: 'Executor of Estate',
  TRUSTEE: 'Trustee',
  OTHER: 'Other',
};

const MARITAL_LABELS = {
  IN_COP: 'Married in Community of Property',
  OUT_COP: 'Married Out of Community of Property',
  OUT_COP_ACCRUAL: 'Married Out of Community of Property with Accrual',
  SINGLE: 'Unmarried / Single',
  TRIBAL: 'Customary Union',
  UNKNOWN: 'Not Disclosed',
};

const FIA_COLORS = {
  expired:   'bg-red-100 text-red-700',
  expiring:  'bg-yellow-100 text-yellow-700',
  valid:     'bg-green-100 text-green-700',
  unverified:'bg-gray-100 text-gray-500',
};

// Fee calculator — UI line key → backend override key mapping.
// Lets the user override a line item by its display name while the backend
// receives the legacy override field it knows.
const OVERRIDE_KEY_MAP = {
  conveyancing_fee: 'transfer_fee',
  bond_attorney_fee: 'bond_fee',
  cancellation_fee: 'cancellation_fee',
  deeds_filing_fee: 'deeds_registry_filing_fee',
  deeds_search_fee: 'search_fee',
};

const BUYER_TYPES = [
  ['NATURAL_PERSON', 'Natural Person'],
  ['COMPANY', 'Company'],
  ['TRUST', 'Trust'],
];

const BLANK_CALC_INPUTS = {
  buyer_type: 'NATURAL_PERSON',
  buyer_is_citizen: true,
  seller_is_vat_vendor: false,
  is_tribal_land: false,
  vat_rate: 14,
};

const BLANK_PARTY = {
  role: 'BUYER', full_name: '', id_number: '', id_type: 'Omang',
  nationality: 'Botswana', is_citizen: true, is_entity: false,
  entity_registration_number: '',
  marital_regime: 'UNKNOWN', spouse_name: '', spouse_id_number: '',
  spouse_consent_obtained: false, spouse_consent_date: '',
  fia_id_document_expiry: '', fia_proof_of_address_expiry: '',
  address: '', email: '', phone: '', notes: '',
};

function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <h4 className="text-sm font-semibold text-gray-800 border-b border-gray-100 pb-2">{title}</h4>
      {children}
    </div>
  );
}

/**
 * One column of the two-card fee breakdown: a table of line items with
 * editable excl-VAT amounts (where overridable), explicit VAT column, and
 * a subtotal row. Styled to match the Billing component cards.
 */
function CostTreeCard({ title, subtitle, lines, subtotal, feeOverrides, toggleOverride, updateOverride }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-4 pb-3 border-b-2 border-blue-600">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
              <th scope="col" className="text-left py-2 pr-2 font-medium">Description</th>
              <th scope="col" className="text-right py-2 px-2 font-medium">Excl. VAT</th>
              <th scope="col" className="text-right py-2 px-2 font-medium">VAT</th>
              <th scope="col" className="text-right py-2 pl-2 font-medium">Incl. VAT</th>
              <th scope="col" className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={5} className="py-4 text-center text-gray-400 text-xs">No line items</td></tr>
            )}
            {lines.map(line => {
              const backendKey = OVERRIDE_KEY_MAP[line.key];
              const overridable = Boolean(backendKey);
              const isOvr = overridable && backendKey in feeOverrides;
              const rowCls = isOvr ? 'bg-amber-50' : '';
              return (
                <tr key={line.key} className={`border-b border-gray-50 ${rowCls}`}>
                  <td className="py-2.5 pr-2 text-gray-700">
                    {line.label}
                    {isOvr && <span className="ml-1 text-amber-600 text-xs font-medium">(edited)</span>}
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums">
                    {isOvr ? (
                      <input type="number" step="0.01" min="0"
                        className="w-28 text-right border border-amber-300 rounded px-2 py-0.5 text-sm bg-white"
                        value={feeOverrides[backendKey]}
                        onChange={e => updateOverride(backendKey, e.target.value)} />
                    ) : (
                      <span className="text-gray-800">{formatCurrency(line.amount_excl_vat)}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums text-gray-600">
                    {line.vat_applicable ? formatCurrency(line.vat) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-2.5 pl-2 text-right tabular-nums font-medium text-gray-900">
                    {formatCurrency(line.amount_incl_vat)}
                  </td>
                  <td className="py-2.5 text-right">
                    {overridable && (
                      <button onClick={() => toggleOverride(backendKey, line.amount_excl_vat)}
                        title={isOvr ? 'Reset to formula' : 'Override this line'}
                        className={`w-6 h-6 flex items-center justify-center rounded text-xs ${isOvr ? 'text-amber-600 hover:bg-amber-100' : 'text-gray-400 hover:bg-gray-200'}`}>
                        {isOvr ? '🔓' : '✏️'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="font-semibold text-gray-900 border-t-2 border-gray-200">
              <td className="py-3 pr-2">Subtotal</td>
              <td className="py-3 px-2 text-right tabular-nums">{formatCurrency(subtotal?.subtotal_excl_vat || 0)}</td>
              <td className="py-3 px-2 text-right tabular-nums">{formatCurrency(subtotal?.subtotal_vat || 0)}</td>
              <td className="py-3 pl-2 text-right tabular-nums text-blue-700">{formatCurrency(subtotal?.subtotal_incl_vat || 0)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ConveyancingDetailPanel({ authAxios, showFormMessage, caseId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [activeSection, setActiveSection] = useState('overview');

  const [parties, setParties] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [fiaStatus, setFiaStatus] = useState([]);
  const [feeResult, setFeeResult] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeOverrides, setFeeOverrides] = useState({});  // { field: value } user-overridden line items
  const [calcInputs, setCalcInputs] = useState(BLANK_CALC_INPUTS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [estimatePdfLoading, setEstimatePdfLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState(null);

  const [showPartyForm, setShowPartyForm] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [partyForm, setPartyForm] = useState(BLANK_PARTY);
  const [partySaving, setPartySaving] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const d = await fetchConveyancingDetailByCase(authAxios, caseId);
      setDetail(d);
      if (d) {
        setForm(d);
        const [p, f, props] = await Promise.all([
          fetchConveyancingParties(authAxios, d.id),
          fetchConveyancingFiaStatus(authAxios, d.id).catch(() => []),
          fetchProperties(authAxios).catch(() => []),
        ]);
        const partiesList = Array.isArray(p) ? p : [];
        setParties(partiesList);
        setFiaStatus(Array.isArray(f) ? f : []);
        setPropertyOptions(Array.isArray(props) ? props : []);

        // Seed calculator inputs from the stored snapshot (if any) so the
        // user sees the same values that produced the last calculation.
        const snap = d.calculation_snapshot || {};
        const buyer = partiesList.find(pt => pt.role === 'BUYER');
        let buyerType = snap.buyer_type;
        if (!buyerType) {
          buyerType = buyer?.is_entity ? 'COMPANY' : 'NATURAL_PERSON';
        }
        setCalcInputs({
          buyer_type: buyerType,
          buyer_is_citizen: snap.buyer_is_citizen ?? (buyer?.is_citizen ?? true),
          seller_is_vat_vendor: snap.seller_is_vat_vendor ?? false,
          is_tribal_land: snap.is_tribal_land ?? false,
          vat_rate: Number(snap.vat_rate) || 14,
        });
        if (snap.transfer_costs || snap.attorney_fees) {
          setFeeResult(snap);
          setFeeOverrides(snap.overrides || {});
        }
      }
    } catch {
      showFormMessage?.('error', 'Failed to load conveyancing detail.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, caseId, showFormMessage]);

  useEffect(() => { load(); }, [load]);

  const handleSaveDetail = async () => {
    if (!detail) return;
    // Guard against negative monetary values — these are always nonsensical.
    const monetaryFields = ['property_value', 'bond_amount', 'cancellation_amount'];
    for (const fieldName of monetaryFields) {
      const val = form[fieldName];
      if (val !== '' && val !== null && val !== undefined && Number(val) < 0) {
        showFormMessage?.('error', `${fieldName.replace(/_/g, ' ')} cannot be negative.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      const nullableFields = [
        'property', 'property_value', 'bond_amount', 'cancellation_amount',
        'burs_receipt_date', 'rates_clearance_expiry', 'levy_clearance_expiry',
        'lodgement_date', 'registration_date', 'expected_registration_date',
        'fica_kyc_verified_at',
      ];
      nullableFields.forEach(k => { if (!payload[k]) payload[k] = null; });
      await updateConveyancingDetail(authAxios, detail.id, payload);
      showFormMessage?.('success', 'Conveyancing detail saved.');
      load();
    } catch (err) {
      const msg = err.response?.data?.detail ?? JSON.stringify(err.response?.data ?? err.message);
      showFormMessage?.('error', `Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCalculateFees = async (useOverrides = false) => {
    if (!detail) return;
    setFeeLoading(true);
    try {
      const payload = {
        property_value: form.property_value || 0,
        bond_amount: form.bond_amount || null,
        cancellation_amount: form.cancellation_amount || null,
        buyer_type: calcInputs.buyer_type,
        buyer_is_citizen: calcInputs.buyer_is_citizen,
        seller_is_vat_vendor: calcInputs.seller_is_vat_vendor,
        is_tribal_land: calcInputs.is_tribal_land,
        vat_rate: Number(calcInputs.vat_rate) || 14,
      };
      // Attach overrides if saving with edits
      if (useOverrides && Object.keys(feeOverrides).length > 0) {
        payload.overrides = feeOverrides;
      }
      const result = await calculateConveyancingFees(authAxios, detail.id, payload);
      setFeeResult(result);
      // Populate overrides from result if any existed
      setFeeOverrides(result.overrides || {});
    } catch {
      showFormMessage?.('error', 'Fee calculation failed.');
    } finally {
      setFeeLoading(false);
    }
  };

  const handleRecalculate = () => {
    setFeeOverrides({});
    handleCalculateFees(false);
  };

  const handleSaveSnapshot = () => {
    handleCalculateFees(true);
  };

  const toggleOverride = (field, currentValue) => {
    setFeeOverrides(prev => {
      if (field in prev) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: currentValue };
    });
  };

  const updateOverride = (field, value) => {
    setFeeOverrides(prev => ({ ...prev, [field]: value }));
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    setPdfLoading(true);
    try {
      const blob = await fetchConveyancingCostLetterPdf(authAxios, detail.id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cost-letter-${detail.case_number ?? detail.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showFormMessage?.('error', 'PDF generation failed.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadEstimatePdf = async () => {
    if (!detail) return;
    setEstimatePdfLoading(true);
    try {
      const blob = await fetchConveyancingFeeSnapshotPdf(authAxios, detail.id);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cost-estimate-${detail.case_number ?? detail.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showFormMessage?.('error', 'Cost estimate PDF generation failed. Run Calculate Fees first.');
    } finally {
      setEstimatePdfLoading(false);
    }
  };

  const cinpt = (k, v) => setCalcInputs(c => ({ ...c, [k]: v }));

  const handleDownloadClosingStatement = async () => {
    if (!detail) return;
    try {
      const resp = await downloadClosingStatement(authAxios, detail.id);
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `closing-statement-${detail.case_number ?? detail.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showFormMessage?.('error', 'Failed to generate closing statement.');
    }
  };

  const handleDownloadTD1 = async () => {
    if (!caseId) return;
    try {
      const resp = await downloadBursTD1Pdf(authAxios, caseId);
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TD1-${detail?.case_number ?? caseId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showFormMessage?.('error', 'Failed to generate BURS TD1 declaration.');
    }
  };

  // ── fetchPdf thunks for SendPdfButton (normalise to Blob) ─────────────────
  const fetchCostLetterPdfBlob = useCallback(async () => {
    if (!detail) throw new Error('No conveyancing detail loaded.');
    const data = await fetchConveyancingCostLetterPdf(authAxios, detail.id);
    return data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
  }, [authAxios, detail]);

  const fetchCostEstimatePdfBlob = useCallback(async () => {
    if (!detail) throw new Error('No conveyancing detail loaded.');
    const data = await fetchConveyancingFeeSnapshotPdf(authAxios, detail.id);
    return data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
  }, [authAxios, detail]);

  const fetchClosingStatementPdfBlob = useCallback(async () => {
    if (!detail) throw new Error('No conveyancing detail loaded.');
    const resp = await downloadClosingStatement(authAxios, detail.id);
    const data = resp?.data ?? resp;
    return data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
  }, [authAxios, detail]);

  const fetchTD1PdfBlob = useCallback(async () => {
    if (!caseId) throw new Error('No case selected.');
    const resp = await downloadBursTD1Pdf(authAxios, caseId);
    const data = resp?.data ?? resp;
    return data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
  }, [authAxios, caseId]);

  const caseLabel = detail?.case_number ?? caseId ?? detail?.id ?? '';

  const handleCreateInvoice = async () => {
    if (!detail) return;
    setInvoiceLoading(true);
    setCreatedInvoice(null);
    try {
      const result = await createConveyancingInvoice(authAxios, detail.id);
      setCreatedInvoice(result);
      showFormMessage?.('success', `Draft invoice ${result.invoice_number} created (BWP ${result.total_amount}). Find it in the Billing tab.`);
    } catch (err) {
      const msg = err.response?.data?.detail ?? JSON.stringify(err.response?.data ?? err.message);
      showFormMessage?.('error', `Invoice creation failed: ${msg}`);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const openPartyCreate = () => { setEditingParty(null); setPartyForm({ ...BLANK_PARTY, conveyancing_detail: detail?.id }); setShowPartyForm(true); };
  const openPartyEdit = (p) => { setEditingParty(p); setPartyForm({ ...BLANK_PARTY, ...p, conveyancing_detail: detail?.id }); setShowPartyForm(true); };

  const handleSaveParty = async (e) => {
    e.preventDefault();
    setPartySaving(true);
    try {
      const payload = { ...partyForm };
      ['spouse_consent_date', 'fia_id_document_expiry', 'fia_proof_of_address_expiry'].forEach(k => {
        if (!payload[k]) payload[k] = null;
      });
      if (editingParty) {
        await updateConveyancingParty(authAxios, editingParty.id, payload);
      } else {
        await createConveyancingParty(authAxios, payload);
      }
      setShowPartyForm(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail ?? JSON.stringify(err.response?.data ?? err.message);
      showFormMessage?.('error', `Save failed: ${msg}`);
    } finally {
      setPartySaving(false);
    }
  };

  const handleDeleteParty = async (p) => {
    try {
      await deleteConveyancingParty(authAxios, p.id);
      load();
    } catch {
      showFormMessage?.('error', 'Failed to delete party.');
    }
  };

  const pfld = (k, v) => setPartyForm(f => ({ ...f, [k]: v }));
  const dfld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'parties', label: `Parties (${parties.length})` },
    { id: 'instruments', label: 'Instruments' },
    { id: 'fees', label: 'Fee Calculator' },
  ];

  if (loading) return <div className="text-center py-12 text-gray-400">Loading conveyancing detail…</div>;
  if (!detail) return (
    <div className="text-center py-12 text-gray-400">
      <p>No conveyancing detail found for this matter.</p>
      <p className="text-sm mt-1">It will be created automatically when the case is saved as type "Conveyancing".</p>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Sub-navigation */}
      <div className="flex gap-1 border-b border-gray-200">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeSection === s.id ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {activeSection === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Registered Property — link this matter to a Property record.
              Without it, BURS TD1, Cost Letter and Closing Statement render
              Plot/District/Deed No. as em-dashes. */}
          <SectionCard title="Registered Property">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Link to Property Record</label>
                <select
                  value={form.property ?? ''}
                  onChange={e => dfld('property', e.target.value ? Number(e.target.value) : null)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">— Select a registered plot —</option>
                  {propertyOptions.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.plot_number}
                      {p.district ? ` · ${p.district}` : ''}
                      {p.deed_of_transfer_number ? ` · ${p.deed_of_transfer_number}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Pick a plot from the Property Register. Adds plot number, district, deed details, and (if set) the FIA-linked owner client to all PDFs.
                </p>
              </div>

              {form.property ? (() => {
                const sel = propertyOptions.find(p => p.id === Number(form.property));
                if (!sel) return null;
                return (
                  <div className="rounded-md bg-blue-50 border border-blue-100 p-3 text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-gray-500">Plot</span><span className="font-medium text-gray-800">{sel.plot_number}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">District / Township</span><span className="text-gray-800">{[sel.district, sel.township].filter(Boolean).join(' / ') || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Existing Deed No.</span><span className="font-mono text-gray-800">{sel.deed_of_transfer_number || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Tenure</span><span className="text-gray-800">{sel.tenure_type || '—'}</span></div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Registered Owner</span>
                      <span className="text-gray-800">
                        {sel.registered_owner_display || sel.registered_owner_name || '—'}
                        {sel.registered_owner_client && (
                          <span className="ml-1 px-1.5 py-0.5 rounded bg-green-100 text-green-700" title="FIA chain inherited from client record">FIA ✓</span>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })() : (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-2">
                  No property linked. BURS TD1, Cost Letter and Closing Statement will show plot details as em-dashes until a property is selected.
                </p>
              )}
            </div>
          </SectionCard>

          <SectionCard title="Transaction Details">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Transaction Type</label>
                <select value={form.transaction_type ?? 'TRANSFER'} onChange={e => dfld('transaction_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {TRANSACTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Risk Rating</label>
                <select value={form.risk_rating ?? 'LOW'} onChange={e => dfld('risk_rating', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {RISK_RATINGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              {[
                ['Property Value (BWP)', 'property_value', 'number'],
                ['Bond Amount (BWP)', 'bond_amount', 'number'],
                ['Cancellation Amount (BWP)', 'cancellation_amount', 'number'],
              ].map(([label, key, type]) => {
                const numericValue = form[key];
                const formattedHint =
                  numericValue !== '' && numericValue !== null && numericValue !== undefined && !Number.isNaN(Number(numericValue))
                    ? formatCurrency(Number(numericValue))
                    : null;
                return (
                  <div key={key}>
                    <label htmlFor={`conv-${key}`} className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                    <input
                      id={`conv-${key}`}
                      type={type}
                      step="0.01"
                      min="0"
                      value={form[key] ?? ''}
                      onChange={e => dfld(key, e.target.value)}
                      aria-describedby={formattedHint ? `conv-${key}-hint` : undefined}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    {formattedHint && (
                      <div id={`conv-${key}-hint`} className="text-xs text-gray-500 mt-1">
                        {formattedHint}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionCard>

          <SectionCard title="Clearances & Dates">
            <div className="grid grid-cols-2 gap-4">
              {[
                ['BURS Receipt No.', 'burs_receipt_number', 'text'],
                ['BURS Receipt Date', 'burs_receipt_date', 'date'],
                ['Rates Clearance No.', 'rates_clearance_number', 'text'],
                ['Rates Clearance Expiry', 'rates_clearance_expiry', 'date'],
                ['Lodgement Date', 'lodgement_date', 'date'],
                ['Lodgement Number', 'lodgement_number', 'text'],
                ['Registration Date', 'registration_date', 'date'],
                ['Expected Reg. Date', 'expected_registration_date', 'date'],
              ].map(([label, key, type]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                  <input type={type} value={form[key] ?? ''} onChange={e => dfld(key, e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="FIA / KYC Gate">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.fica_kyc_complete ?? false}
                  onChange={e => dfld('fica_kyc_complete', e.target.checked)}
                  className="w-4 h-4 accent-blue-600" />
                <span className="text-sm font-medium text-gray-700">FIA / KYC Complete</span>
              </label>
              {!form.fica_kyc_complete && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">Gate Blocked</span>
              )}
              {form.fica_kyc_complete && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Cleared</span>
              )}
            </div>
            {fiaStatus.length > 0 && (
              <div className="space-y-2 mt-2">
                {fiaStatus.map(item => (
                  <div key={item.id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{item.display_name}</span>
                    <div className="flex gap-1">
                      {item.id_doc_status && (
                        <span className={`px-2 py-0.5 rounded-full text-xs ${FIA_COLORS[item.id_doc_status] ?? ''}`}>ID: {item.id_doc_status}</span>
                      )}
                      {item.poa_status && (
                        <span className={`px-2 py-0.5 rounded-full text-xs ${FIA_COLORS[item.poa_status] ?? ''}`}>POA: {item.poa_status}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Calculated Fees">
            {form.calculation_snapshot && Object.keys(form.calculation_snapshot).length > 0 ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Attorney Fee</span><span className="font-medium">{formatCurrency(form.calculated_attorney_fee)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">VAT on Fee</span><span className="font-medium">{formatCurrency(form.calculated_vat_on_fee)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Transfer Duty</span><span className="font-medium">{formatCurrency(form.calculated_transfer_duty)}</span></div>
                <div className="border-t border-gray-100 pt-2 flex justify-between font-semibold">
                  <span>Total Deposit Required</span>
                  <span className="text-blue-700">{formatCurrency(form.calculation_snapshot?.total_deposit)}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">Run the fee calculator to populate these figures.</p>
            )}
          </SectionCard>

          <div className="lg:col-span-2 flex justify-end gap-3">
            <button onClick={handleSaveDetail} disabled={saving}
              className="px-5 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
              {saving ? 'Saving…' : 'Save Detail'}
            </button>
          </div>
        </div>
      )}

      {/* ── Parties ── */}
      {activeSection === 'parties' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={openPartyCreate}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
              + Add Party
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {parties.map(p => {
              const fiaItem = fiaStatus.find(f => f.id === p.id);
              return (
                <div key={p.id} className="border border-gray-200 rounded-xl bg-white p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        {ROLE_LABELS[p.role] ?? p.role}
                      </span>
                      <div className="mt-1 font-medium text-gray-900">{p.display_name ?? p.full_name}</div>
                      {p.id_number && <div className="text-xs text-gray-500 font-mono">{p.id_type}: {p.id_number}</div>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openPartyEdit(p)} className="text-xs text-blue-600 hover:underline">Edit</button>
                      <button onClick={() => handleDeleteParty(p)} className="text-xs text-red-500 hover:underline">Remove</button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="text-gray-500">{MARITAL_LABELS[p.marital_regime] ?? p.marital_regime}</span>
                    {p.marital_regime === 'IN_COP' && p.spouse_name && (
                      <span className="text-gray-500">Spouse: {p.spouse_name}
                        {p.spouse_consent_obtained ? ' ✓' : ' — consent pending'}
                      </span>
                    )}
                    {p.is_entity && <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Entity</span>}
                    {!p.is_citizen && <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Non-citizen</span>}
                  </div>
                  {fiaItem && (
                    <div className="flex gap-2">
                      {fiaItem.id_doc_status && <span className={`text-xs px-2 py-0.5 rounded-full ${FIA_COLORS[fiaItem.id_doc_status]}`}>ID: {fiaItem.id_doc_status}</span>}
                      {fiaItem.poa_status && <span className={`text-xs px-2 py-0.5 rounded-full ${FIA_COLORS[fiaItem.poa_status]}`}>POA: {fiaItem.poa_status}</span>}
                    </div>
                  )}
                </div>
              );
            })}
            {parties.length === 0 && <p className="text-sm text-gray-400">No parties added yet.</p>}
          </div>
        </div>
      )}

      {/* ── Instruments ── */}
      {activeSection === 'instruments' && (
        <DeedInstrumentsList
          authAxios={authAxios}
          showFormMessage={showFormMessage}
          conveyancingDetailId={detail.id}
        />
      )}

      {/* ── Fee Calculator (redesigned: SA-benchmarked two-column layout) ── */}
      {activeSection === 'fees' && (
        <div className="space-y-6">

          {/* Inputs card */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Calculator Inputs</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Property and bond amounts come from the Overview tab. Adjust these only for the calculation.
                </p>
              </div>
              {feeResult?.fee_schedule?.name && (
                <span className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                  Tariff: {feeResult.fee_schedule.name}
                  {feeResult.fee_schedule.effective_date && ` · eff. ${feeResult.fee_schedule.effective_date}`}
                </span>
              )}
            </div>

            {/* Read-only summary of the values pulled from Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5 p-3 bg-gray-50 rounded-md">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Purchase Price</div>
                <div className="text-lg font-semibold text-gray-800">{formatCurrency(form.property_value || 0)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Bond Amount</div>
                <div className="text-lg font-semibold text-gray-800">
                  {Number(form.bond_amount) > 0 ? formatCurrency(form.bond_amount) : <span className="text-sm text-gray-400 font-normal">— cash purchase</span>}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Cancellation Amount</div>
                <div className="text-lg font-semibold text-gray-800">
                  {Number(form.cancellation_amount) > 0 ? formatCurrency(form.cancellation_amount) : <span className="text-sm text-gray-400 font-normal">— none</span>}
                </div>
              </div>
            </div>

            {/* Buyer / seller classification */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Buyer Type</label>
                <select value={calcInputs.buyer_type} onChange={e => cinpt('buyer_type', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                  {BUYER_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">Affects transfer duty rate.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Buyer is a Botswana citizen</label>
                <label className="flex items-center gap-2 cursor-pointer mt-2 select-none">
                  <input type="checkbox" checked={calcInputs.buyer_is_citizen}
                    onChange={e => cinpt('buyer_is_citizen', e.target.checked)}
                    disabled={calcInputs.buyer_type !== 'NATURAL_PERSON'}
                    className="w-4 h-4 accent-blue-600" />
                  <span className="text-sm text-gray-700">{calcInputs.buyer_is_citizen ? 'Citizen' : 'Non-citizen'}</span>
                </label>
                {calcInputs.buyer_type !== 'NATURAL_PERSON' && (
                  <p className="text-xs text-gray-400 mt-1">N/A for entities.</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Seller is a VAT vendor</label>
                <label className="flex items-center gap-2 cursor-pointer mt-2 select-none">
                  <input type="checkbox" checked={calcInputs.seller_is_vat_vendor}
                    onChange={e => cinpt('seller_is_vat_vendor', e.target.checked)}
                    className="w-4 h-4 accent-blue-600" />
                  <span className="text-sm text-gray-700">{calcInputs.seller_is_vat_vendor ? 'Yes — VAT applies' : 'No'}</span>
                </label>
                <p className="text-xs text-gray-400 mt-1">Replaces transfer duty with 14% VAT on price.</p>
              </div>
            </div>

            {/* Advanced section (collapsible) */}
            <div className="mt-4">
              <button type="button" onClick={() => setShowAdvanced(v => !v)}
                className="text-sm text-blue-600 hover:underline">
                {showAdvanced ? '▾ Hide advanced options' : '▸ Advanced options'}
              </button>
              {showAdvanced && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 p-4 border border-gray-200 rounded-md bg-gray-50">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">VAT Rate (%)</label>
                    <input type="number" step="0.5" min="0" max="100" value={calcInputs.vat_rate}
                      onChange={e => cinpt('vat_rate', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Tribal / Customary Land</label>
                    <label className="flex items-center gap-2 cursor-pointer mt-2 select-none">
                      <input type="checkbox" checked={calcInputs.is_tribal_land}
                        onChange={e => cinpt('is_tribal_land', e.target.checked)}
                        className="w-4 h-4 accent-blue-600" />
                      <span className="text-sm text-gray-700">Apply tribal-land exemption</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-gray-100">
              <button onClick={handleCalculateFees} disabled={feeLoading}
                className="px-5 py-2 text-sm bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-60">
                {feeLoading ? 'Calculating…' : 'Calculate Fees'}
              </button>
              <button onClick={handleDownloadEstimatePdf} disabled={estimatePdfLoading || !feeResult}
                title={!feeResult ? 'Run Calculate Fees first' : 'Two-column SA-style cost estimate'}
                className="px-5 py-2 text-sm bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-60">
                {estimatePdfLoading ? 'Generating…' : '↓ Cost Estimate PDF'}
              </button>
              {feeResult && (
                <SendPdfButton
                  authAxios={authAxios}
                  showFormMessage={showFormMessage}
                  fetchPdf={fetchCostEstimatePdfBlob}
                  defaultFilename={`cost-estimate-${caseLabel}.pdf`}
                  defaultSubject={`Conveyancing Cost Estimate — ${caseLabel}`}
                  defaultBody={`Please find attached the pro forma cost estimate for matter ${caseLabel}.`}
                  documentKind="COST_ESTIMATE"
                  objectId={detail?.case_id || detail?.id}
                  buttonLabel="Email Estimate"
                  buttonClassName="px-3 py-2 text-xs border border-blue-300 text-blue-700 bg-white rounded-md hover:bg-blue-50"
                />
              )}
              <button onClick={handleDownloadPdf} disabled={pdfLoading}
                className="px-5 py-2 text-sm border border-gray-300 text-gray-700 bg-white rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-60">
                {pdfLoading ? 'Generating…' : '↓ Pro Forma Cost Letter'}
              </button>
              <SendPdfButton
                authAxios={authAxios}
                showFormMessage={showFormMessage}
                fetchPdf={fetchCostLetterPdfBlob}
                defaultFilename={`cost-letter-${caseLabel}.pdf`}
                defaultSubject={`Pro Forma Cost Letter — ${caseLabel}`}
                defaultBody={`Please find attached the pro forma cost letter for matter ${caseLabel}.`}
                documentKind="COST_LETTER"
                objectId={detail?.case_id || detail?.id}
                buttonLabel="Email Cost Letter"
                buttonClassName="px-3 py-2 text-xs border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50"
              />
              <button onClick={handleDownloadClosingStatement}
                className="px-5 py-2 text-sm border border-gray-300 text-gray-700 bg-white rounded-md shadow-sm hover:bg-gray-50">
                ↓ Closing Statement
              </button>
              <SendPdfButton
                authAxios={authAxios}
                showFormMessage={showFormMessage}
                fetchPdf={fetchClosingStatementPdfBlob}
                defaultFilename={`closing-statement-${caseLabel}.pdf`}
                defaultSubject={`Closing Statement — ${caseLabel}`}
                defaultBody={`Please find attached the closing statement for matter ${caseLabel}.`}
                documentKind="CLOSING_STATEMENT"
                objectId={detail?.case_id || detail?.id}
                buttonLabel="Email Closing"
                buttonClassName="px-3 py-2 text-xs border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50"
              />
              <button onClick={handleDownloadTD1}
                className="px-5 py-2 text-sm border border-amber-300 text-amber-700 bg-white rounded-md shadow-sm hover:bg-amber-50">
                ↓ BURS TD1
              </button>
              <SendPdfButton
                authAxios={authAxios}
                showFormMessage={showFormMessage}
                fetchPdf={fetchTD1PdfBlob}
                defaultFilename={`TD1-${caseLabel}.pdf`}
                defaultSubject={`BURS TD1 Declaration — ${caseLabel}`}
                defaultBody={`Please find attached the BURS TD1 transfer-duty declaration for matter ${caseLabel}.`}
                documentKind="TD1"
                objectId={caseId}
                buttonLabel="Email TD1"
                buttonClassName="px-3 py-2 text-xs border border-amber-300 text-amber-700 bg-white rounded-md hover:bg-amber-50"
              />
              {/* End of PDF send buttons */}
              <button onClick={handleCreateInvoice}
                disabled={invoiceLoading || !(detail?.calculation_snapshot && Object.keys(detail.calculation_snapshot).length > 0)}
                title={!(detail?.calculation_snapshot && Object.keys(detail.calculation_snapshot).length > 0) ? 'Run Calculate Fees first' : 'Create a draft invoice in Billing from the fee snapshot'}
                className="px-5 py-2 text-sm bg-emerald-600 text-white rounded-md shadow-sm hover:bg-emerald-700 disabled:opacity-60">
                {invoiceLoading ? 'Creating…' : '+ Post to Billing'}
              </button>
            </div>

            {createdInvoice && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-800 flex items-center gap-2">
                <span>✓</span>
                <span>Invoice <strong>{createdInvoice.invoice_number}</strong> created as Draft — total BWP {Number(createdInvoice.total_amount).toLocaleString('en-BW', { minimumFractionDigits: 2 })}. Open the <strong>Billing</strong> tab to review and confirm.</span>
              </div>
            )}
          </div>

          {/* Results — two-column cost trees */}
          {feeResult && (feeResult.transfer_costs || feeResult.bond_costs) && (
            <>
              {Object.keys(feeOverrides).length > 0 && (
                <div className="flex justify-end gap-2">
                  <button onClick={handleRecalculate}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-600 bg-white hover:bg-gray-50">
                    Reset to Formula
                  </button>
                  <button onClick={handleSaveSnapshot} disabled={feeLoading}
                    className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
                    Save Snapshot
                  </button>
                </div>
              )}

              <div className={`grid grid-cols-1 ${(feeResult.bond_costs?.lines?.length ?? 0) > 0 ? 'lg:grid-cols-2' : ''} gap-6`}>
                {/* Transfer Costs card */}
                <CostTreeCard
                  title="Transfer Costs"
                  subtitle="Paid to the transfer attorney"
                  lines={feeResult.transfer_costs?.lines || []}
                  subtotal={feeResult.transfer_costs}
                  feeOverrides={feeOverrides}
                  toggleOverride={toggleOverride}
                  updateOverride={updateOverride}
                />

                {/* Bond Costs card */}
                {(feeResult.bond_costs?.lines?.length ?? 0) > 0 && (
                  <CostTreeCard
                    title="Bond Registration Costs"
                    subtitle="Paid to the bond attorney"
                    lines={feeResult.bond_costs?.lines || []}
                    subtotal={feeResult.bond_costs}
                    feeOverrides={feeOverrides}
                    toggleOverride={toggleOverride}
                    updateOverride={updateOverride}
                  />
                )}
              </div>

              {/* Grand total card */}
              <div className="bg-blue-600 text-white p-6 rounded-md shadow-md flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium opacity-90 uppercase tracking-wide">Total Upfront (Buyer)</div>
                  <div className="text-xs opacity-75 mt-1">Trust deposit required before lodgement</div>
                </div>
                <div className="text-3xl font-bold">
                  {formatCurrency(feeResult.grand_total || feeResult.total_deposit || feeResult.total_due || 0)}
                </div>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-gray-500 italic">
                This is a pro forma estimate. Final figures are confirmed upon registration at the Deeds Registry.
                Excludes occupational rent, pro-rata rates and levies, bank initiation fees, and third-party disbursements not yet known.
              </p>
            </>
          )}

          {!feeResult && (
            <div className="bg-white p-6 rounded-lg shadow-md text-center text-gray-400">
              <p className="text-sm">Run the calculator above to see the cost breakdown.</p>
            </div>
          )}
        </div>
      )}

      {/* Party form modal */}
      {showPartyForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <h3 className="font-semibold">{editingParty ? 'Edit Party' : 'Add Party'}</h3>
              <button onClick={() => setShowPartyForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleSaveParty} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Role *</label>
                <select required value={partyForm.role} onChange={e => pfld('role', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Full Name</label>
                <input value={partyForm.full_name} onChange={e => pfld('full_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">ID Type</label>
                <select value={partyForm.id_type} onChange={e => pfld('id_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {['Omang', 'Passport', 'Company Reg', 'Other'].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">ID Number (Omang)</label>
                <input value={partyForm.id_number} onChange={e => pfld('id_number', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nationality</label>
                <input value={partyForm.nationality} onChange={e => pfld('nationality', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Marital Regime</label>
                <select value={partyForm.marital_regime} onChange={e => pfld('marital_regime', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(MARITAL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={partyForm.is_citizen} onChange={e => pfld('is_citizen', e.target.checked)}
                    className="w-4 h-4 accent-blue-600" />
                  Citizen
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={partyForm.is_entity} onChange={e => pfld('is_entity', e.target.checked)}
                    className="w-4 h-4 accent-blue-600" />
                  Entity (co/trust/CC)
                </label>
              </div>
              {partyForm.marital_regime === 'IN_COP' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Spouse Name</label>
                    <input value={partyForm.spouse_name} onChange={e => pfld('spouse_name', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Spouse ID Number</label>
                    <input value={partyForm.spouse_id_number} onChange={e => pfld('spouse_id_number', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="checkbox" checked={partyForm.spouse_consent_obtained}
                        onChange={e => pfld('spouse_consent_obtained', e.target.checked)}
                        className="w-4 h-4 accent-blue-600" />
                      Spousal Consent Obtained
                    </label>
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">FIA ID Doc Expiry</label>
                <input type="date" value={partyForm.fia_id_document_expiry} onChange={e => pfld('fia_id_document_expiry', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">FIA Proof of Address Expiry</label>
                <input type="date" value={partyForm.fia_proof_of_address_expiry} onChange={e => pfld('fia_proof_of_address_expiry', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <input value={partyForm.phone} onChange={e => pfld('phone', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={partyForm.email} onChange={e => pfld('email', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                <textarea value={partyForm.address} onChange={e => pfld('address', e.target.value)}
                  rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowPartyForm(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={partySaving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
                  {partySaving ? 'Saving…' : editingParty ? 'Save' : 'Add Party'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
